import { Close, KeyboardArrowLeftOutlined } from '@mui/icons-material'
import {
  AppBar,
  Box,
  Button,
  createTheme,
  CssBaseline,
  DialogActions,
  Divider,
  Drawer,
  IconButton,
  SxProps,
  Theme,
  ThemeProvider,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material'

import { generateTheme } from '../../providers/mui-theme-provider'
import { IBasicDialogProps } from '../m3/basic-dialog'

import useStyles from './index.styles'

interface Props extends IBasicDialogProps {
  children: React.ReactNode
  open: boolean
  onClose: () => void
  showCloseIcon?: boolean
  title?: string
  rightIcon?: React.ReactNode
  modalPaperStyle?: React.CSSProperties
  titleSx?: SxProps<Theme>
  showBackIcon?: boolean
  isFullScreen?: boolean
  contentContainerSx?: SxProps<Theme>
}

const FullScreenDrawer = (props: Props) => {
  const {
    children,
    open,
    onClose,
    showCloseIcon = true,
    title = '',
    rightIcon,
    modalPaperStyle,
    titleSx,
    showBackIcon,
    hideCancelButton = true,
    closeLabel,
    onConfirm,
    confirmLoading,
    confirmLabel,
    confirmButton,
    isFullScreen = true,
    contentContainerSx,
  } = props

  const classes = useStyles
  const theme = useTheme()

  return (
    // Force light theme for dialog to ensure it looks correct when used in dark mode screens
    <ThemeProvider theme={createTheme(generateTheme('light'))}>
      <CssBaseline />

      <Drawer
        open={open}
        onClose={onClose}
        anchor="bottom"
        ModalProps={{ sx: classes.modal }}
        slotProps={{
          paper: {
            sx: classes.paper(isFullScreen),
            style: modalPaperStyle || {},
          },
        }}
      >
        <Box sx={classes.headerContainer}>
          <AppBar color="transparent" position="relative" sx={classes.appBar}>
            <Toolbar sx={classes.toolbar}>
              {showCloseIcon && (
                <IconButton edge="start" onClick={onClose} aria-label="close" sx={{ color: theme.palette.text.secondary }}>
                  <Close />
                </IconButton>
              )}

              {showBackIcon && !showCloseIcon && (
                <IconButton edge="start" onClick={onClose} aria-label="back" sx={{ color: theme.palette.text.secondary }}>
                  <KeyboardArrowLeftOutlined />
                </IconButton>
              )}

              {!showCloseIcon && !showBackIcon && <div style={classes.spacer} />}

              <Typography
                variant="h6"
                component="div"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 'calc(100% - 96px)', // 96px accounts for the width of the close/back button to ensure title is centered when either is present
                  textAlign: 'center',
                  ...titleSx,
                }}
              >
                {title}
              </Typography>

              {rightIcon ? <div>{rightIcon}</div> : <div style={classes.spacer} />}
            </Toolbar>
          </AppBar>

          <Divider />
        </Box>

        <Box sx={[classes.contentContainer, ...(Array.isArray(contentContainerSx) ? contentContainerSx : [contentContainerSx])]}>{children}</Box>

        {(!hideCancelButton || confirmLabel || confirmButton) && (
          <DialogActions sx={{ ...classes.dialogActions(theme, !hideCancelButton && (!!confirmLabel || !!confirmButton)) }}>
            {!hideCancelButton && <Button onClick={onClose}>{closeLabel || 'Cancel'}</Button>}
            {confirmLabel && !confirmButton && (
              <Button variant="contained" disableElevation onClick={onConfirm} disabled={confirmLoading} loading={confirmLoading}>
                {confirmLabel}
              </Button>
            )}
            {!confirmLabel && confirmButton && confirmButton}
          </DialogActions>
        )}
      </Drawer>
    </ThemeProvider>
  )
}

export default FullScreenDrawer
